import type { RecordView } from '@kit/sync/types/record-view';
import { Check } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import type { ShoppingItem } from '@/entities/shopping-item/schemas/shopping-item';
import { DateField } from '@/shared/components/form/date-field';
import { FormGrid } from '@/shared/components/form/form-grid';
import { MoneyField } from '@/shared/components/form/money-field';
import { SwitchField } from '@/shared/components/form/switch-field';
import { TextField } from '@/shared/components/form/text-field';
import { useTranslation } from '@/shared/i18n/use-translation';
import { today } from '@/shared/lib/dates/today';
import { centsToInput } from '@/shared/lib/money/cents-to-input';
import { parseEuros } from '@/shared/lib/money/parse-euros';
import { ValidationError } from '@/shared/lib/validation-error';
import { Button } from '@/shared/ui/button/button';
import { Dialog } from '@/shared/ui/dialog/dialog';
import { DialogContent } from '@/shared/ui/dialog/dialog-content';
import { DialogDescription } from '@/shared/ui/dialog/dialog-description';
import { DialogFooter } from '@/shared/ui/dialog/dialog-footer';
import { DialogHeader } from '@/shared/ui/dialog/dialog-header';
import { DialogTitle } from '@/shared/ui/dialog/dialog-title';
import { recordPurchase } from '../api/record-purchase';

type BuyDialogProps = {
  readonly view: RecordView<ShoppingItem>;
  readonly locations: readonly string[];
  readonly onClose: () => void;
};

export function BuyDialog({ view, locations, onClose }: BuyDialogProps) {
  const { t } = useTranslation();
  const [price, setPrice] = useState(centsToInput(view.data.estimated_price_cents));
  const [boughtOn, setBoughtOn] = useState(today());
  const [toInventory, setToInventory] = useState(!view.data.item_id);
  const [location, setLocation] = useState('');
  const [expiresOn, setExpiresOn] = useState('');
  const [error, setError] = useState<string>();

  async function confirm() {
    const priceCents = parseEuros(price);
    if (Number.isNaN(priceCents)) {
      setError(t('Invalid amount'));
      return;
    }
    try {
      await recordPurchase(view, {
        priceCents,
        boughtOn,
        toInventory: toInventory ? { location, expiresOn } : null,
      });
      toast.success(toInventory ? t('Bought and added to the inventory') : t('Marked as bought'));
      onClose();
    } catch (caught) {
      setError(caught instanceof ValidationError ? caught.issues[0]?.message : String(caught));
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('Bought: {name}', { name: view.data.name })}</DialogTitle>
          <DialogDescription>
            {t('Write down what it really cost to sharpen the next estimates.')}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <FormGrid>
            <MoneyField label={t('Actual price')} value={price} onChange={setPrice} error={error} />
            <DateField label={t('Date')} value={boughtOn} onChange={setBoughtOn} />
          </FormGrid>
          <SwitchField
            label={t('Add to the inventory')}
            hint={t('Creates the product with the quantity, the store and the price')}
            checked={toInventory}
            onChange={setToInventory}
          />
          {toInventory && (
            <FormGrid>
              <TextField
                label={t('Location')}
                value={location}
                onChange={setLocation}
                suggestions={locations}
              />
              <DateField label={t('Expires on')} value={expiresOn} onChange={setExpiresOn} />
            </FormGrid>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button onClick={() => void confirm()}>
            <Check /> {t('Confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
